import React, { Component } from 'react';
import { connect } from 'react-redux';
import fetch from 'isomorphic-fetch';

import InlineVideo from '@spp/inline-video';
import { actions as spidActions } from '@spp/redux-spid';
import VideoPaywallOverlay from './VideoPaywallOverlay';
import { config } from '../../config';
import normalizeDevice from '../../utils/normalize-device';
import { getSupplyType } from '../../ads/helpers';
import hasIpAccess from '../../utils/plussIpAccess';
import getPersonalizedConfig from '../../utils/get-personalized-config';
import duplicateApnKeywords from '../../utils/duplicate-apn-keywords';

// timeout for loading video access
const VIDEO_ACCESS_TIMEOUT = 5000;

/* eslint-disable react/prop-types */
class LazyLoadedVideo extends Component {
    constructor(props) {
        super(props);

        const {
            video: {
                locale,
                vendor,
                shareLink,
                plugins,
                skin,
                playerUrl,
                appnexus: {
                    placementIds: { preroll = {}, bumper = {} } = {},
                    pageType = 'article',
                    publisher = 'vg',
                    publisherGroup = 'schibsted',
                    countryCode = 'no',
                } = {},
                antiAdblock,
                antiAdblockConfigUrl,
                pulse,
            },
            iris: { host } = {},
        } = config;

        const { value: videoTitle } = this.props.title || {};
        const { id: videoId = '', section: { id: tvCategory = '' } = {} } = this.props.videoAsset;
        const isAutoPlay = this.props.startMode === 'silent';

        const { access: { plus } = {} } = this.props.videoAsset;
        let userHasAccess = true;

        // need to check access before rendering
        if (plus === true) {
            userHasAccess = false;
        }

        let assetType = 'video';
        const firstSource =
            this.props.videoAsset.sourceFiles &&
            this.props.videoAsset.sourceFiles.length &&
            this.props.videoAsset.sourceFiles[0];
        if (firstSource && firstSource.width === 0 && firstSource.height === 0) {
            assetType = 'audio';
        }

        this.state = {
            assetType,
            userHasAccess,
            userIsLoaded: false,
            noAds: false,
            ready: false,

            config: {
                locale,
                vendor,
                shareLink,
                plugins,
                skin,
                playerUrl,
                preroll,
                bumper,
                pageType,
                publisher,
                publisherGroup,
                countryCode,
                host,
                antiAdblock,
                antiAdblockConfigUrl,
                pulse,
            },
            videoTitle,
            videoId,
            tvCategory,
            isAutoPlay,
        };
    }

    componentDidMount() {
        setTimeout(() => {
            if (this.state.ready) {
                return;
            }

            this.setState({
                ready: true,
            });
        }, VIDEO_ACCESS_TIMEOUT);
    }

    componentWillReceiveProps(nextProps) {
        const { spid = {} } = nextProps;
        const { ready } = this.state;

        if (this.state.userIsLoaded === false && spid.userId && !this.loadingUser) {
            this.loadingUser = true;
            this.loadAccess(nextProps);
        } else if (!ready && !spid.userId && typeof spid.login === 'function') {
            // set to ready if not logged in
            this.setState({
                ready: true,
            });
        }
    }

    setUserConfigToVideo(access, noAds = false) {
        this.setState(({ userHasAccess }) => ({
            userHasAccess: userHasAccess || access,
            userIsLoaded: true,
            noAds,
            ready: true,
        }));

        this.forceUpdate();
    }

    getPlayerConfig = (videoConfig) => {
        const { access: { plus } = {} } = this.props.videoAsset;

        const playerConfig = {
            ...videoConfig,
            locale: this.state.config.locale,
            vendor: this.state.config.vendor,
            title: this.state.videoTitle,
            sharing: { link: this.state.config.shareLink + this.state.videoId },
            plugins: this.state.config.plugins,
            skin: this.state.config.skin,
            adn: this.adn,
            pulse: this.state.config.pulse,
        };

        // audio asset
        if (this.state.assetType === 'audio') {
            Object.assign(playerConfig, { height: 40, na: true });
        }

        if (plus === true) {
            Object.assign(playerConfig, { token: this.tokenPromiseFunc() });
        }

        return playerConfig;
    };

    getAdSlotsSchedule = (placementId = null, categoryId) => {
        const adformat = this.state.isAutoPlay ? 'bumper' : 'preroll';

        return {
            preroll: duplicateApnKeywords({
                id: placementId,
                'no-sno-adformat': adformat,
                'no-sno-news-tv_category': categoryId,
                'no-sno-publishergroup': this.state.config.publisherGroup,
            }),
        };
    };

    getAppnexusPlacementId = (deviceType) => {
        const placements = this.state.isAutoPlay
            ? this.state.config.bumper
            : this.state.config.preroll;

        if (!placements[deviceType]) {
            throw new Error(`Missing appnexus placement id for device type of ${deviceType}`);
        }

        return placements[deviceType];
    };

    /**
     * check if access to VG+ products and whether to show ads or not
     */
    loadAccess(props = this.props) {
        const { dispatch } = props;

        dispatch(spidActions.hasAnyProduct(config.spid.productIds))
            .then((spidResponse) => {
                const hasProduct = spidResponse && spidResponse.result;
                if (hasProduct) {
                    const { userId, sig } = this.props.spid;

                    this.shouldNotShowAds(userId, sig).then((showNoAds) =>
                        this.setUserConfigToVideo(hasProduct, showNoAds),
                    );
                } else {
                    hasIpAccess().then((hasAccess) => this.setUserConfigToVideo(hasAccess));
                }
            })
            .catch((err) => {
                console.error(err); // eslint-disable-line
                this.setState({
                    ready: true,
                });
            });
    }

    shouldNotShowAds = (userId, sig) =>
        getPersonalizedConfig('vg:hyperion', userId, sig).then((personalizedConfig) => {
            const { vgtvNoAds = 'false' } = personalizedConfig;

            return vgtvNoAds === 'true';
        });

    tokenPromiseFunc = () => {
        const {
            video: { tokenApiUrl },
        } = config;
        return (assetId) => {
            const url = `${tokenApiUrl}/svp/token/${assetId}`;

            // eslint-disable-next-line
            return fetch(url)
                .then((response) => {
                    if (response.status === 200) {
                        return response.json();
                    }
                    return Promise.reject();
                })
                .catch(() => Promise.reject());
        };
    };

    adn = (currentVideoAsset) => {
        const {
            id: currentVideoId = this.state.videoId,
            section: { id: categoryId = 0 },
        } = currentVideoAsset;
        const isFirstVideo = currentVideoId === this.state.videoId;

        const shallSkipPreroll = this.state.isAutoPlay && isFirstVideo;
        const deviceType = normalizeDevice(this.props.device);
        let placementId = shallSkipPreroll ? null : this.getAppnexusPlacementId(deviceType);

        /**
         * Custom placement id for VG+ subscribers
         * https://github.schibsted.io/vg/hyperion-vg/issues/1409
         */
        if (this.state.noAds) {
            placementId = {
                mobile: 14096833,
                tablet: 14096832,
                desktop: 14096831,
            }[deviceType];
        }

        const schedule = this.getAdSlotsSchedule(placementId, categoryId);

        return Promise.resolve({
            adSlots: {
                schedule,
                params: duplicateApnKeywords({
                    'aa-sch-country_code': this.state.config.countryCode,
                    'aa-sch-supply_type': getSupplyType(this.props.device),
                    'aa-sch-publisher': this.state.config.publisher,
                    'aa-sch-page_type': this.state.config.pageType,
                }),
            },
        });
    };

    render() {
        const { ready, userHasAccess, userIsLoaded, config: videoConfig } = this.state;
        const { videoAsset, device, subtype, spid, ...rest } = this.props;

        if (!ready) {
            // render placeholder
            return <div style={{ paddingBottom: '56.25%', backgroundColor: '#f1f1f1' }} />;
        }

        if (!userHasAccess) {
            return (
                <VideoPaywallOverlay
                    isTop={subtype === 'top'}
                    videoAsset={videoAsset}
                    isLoggedIn={userIsLoaded}
                    spid={spid}
                    device={device}
                />
            );
        }

        let inlineVideoMarkup = (
            <InlineVideo
                getPlayerConfig={this.getPlayerConfig}
                reduxPath="video"
                videoAsset={this.props.videoAsset}
                autoplayNext
                apiUrl={videoConfig.host}
                videoPlayerUrl={videoConfig.playerUrl}
                antiAdblock={videoConfig.antiAdblock}
                antiAdblockConfigUrl={videoConfig.antiAdblockConfigUrl}
                startMode={videoConfig.startMode}
                {...rest}
            />
        );

        if (subtype === 'top') {
            inlineVideoMarkup = <div className="top-video">{inlineVideoMarkup}</div>;
        }

        return inlineVideoMarkup;
    }
}

const enhance = connect((state) => ({
    device: state.device,
    spid: state.spid,
}));

export default enhance(LazyLoadedVideo);



/** WEBPACK FOOTER **
 ** ./src/local-components/article/LazyLoadedVideo.js
 **/